import { NavLink, Outlet } from 'react-router-dom'
import { CalendarOff, ClipboardCheck, AlertTriangle, FileText } from 'lucide-react'
import { useNotice } from '../../lib/notice.js'

/**
 * พื้นที่ของฉัน › ฝ่ายบุคคล — asking for leave, their own evaluation, and warnings
 * issued to them. (การลาออก is deliberately NOT here — see ตั้งค่า.)
 *
 * These were separate top-level tabs, which pushed the /me row to seven and put
 * **ขอลา next to ลาออก** — one syllable apart in Thai, meaning very different things,
 * and the misclick nobody would notice until it had happened. The row is back to five,
 * and the two are now in different places entirely.
 *
 * Pills, not a second underline row — two identical tab rows stacked read as one
 * confused row. Same treatment as ตารางเวร › ทันตแพทย์ and พนักงาน › การลา.
 *
 * ORDER IS BY FREQUENCY, not by importance: ขอลา is the reason most people open this
 * tab, so it is the index.
 */
const TABS = [
  { to: '', end: true, label: 'ขอลา', icon: CalendarOff, hint: 'ขอลากิจ ลาป่วย และดูผลการพิจารณา', badge: 'leave' },
  { to: 'evaluation', label: 'ประเมิน', icon: ClipboardCheck, hint: 'คะแนนประเมินของฉัน และแบบประเมินที่ต้องทำ' },
  { to: 'warning', label: 'หนังสือเตือน', icon: AlertTriangle, hint: 'หนังสือเตือนที่ได้รับ', badge: 'warn' },
  // ขอเอกสาร carries NO badge and never will: nothing here is ever pending, because
  // nothing here waits for a decision. A count next to it would promise a queue that
  // does not exist.
  { to: 'documents', label: 'ขอเอกสาร', icon: FileText, hint: 'หนังสือรับรองเงินเดือน / รับรองการทำงาน — ได้ไฟล์ทันที' },
  // ลาออก is NOT here — it lives in ตั้งค่า, beside the employment facts it changes,
  // and deliberately nowhere near ขอลา.
]

export default function MeHrLayout() {
  // Read-only. Layout.jsx owns the single autoLoad, so mounting this shell does not
  // kick a second identical request.
  const n = useNotice()
  const count = (key) => {
    if (key === 'leave') return n.leave_decided
    if (key === 'warn') return n.warnings_unack
    return 0
  }

  return (
    <div>
      <nav className="flex flex-wrap items-center gap-1 mb-5">
        {TABS.map(({ to, end, label, icon: Icon, hint, badge }) => (
          <NavLink
            key={to || 'index'}
            to={to ? `/me/hr/${to}` : '/me/hr'}
            end={end}
            title={hint}
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors ${
                isActive
                  ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100 font-medium'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
              }`
            }
          >
            <Icon size={13} strokeWidth={1.75} />
            <span>{label}</span>
            {badge && count(badge) > 0 && (
              <span className="ml-0.5 rounded-full bg-rose-500 text-white px-1.5 text-[10.5px] font-semibold leading-[17px]">
                {count(badge)}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  )
}
